import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DynamicOffcanvas from '../Dynamic/DynamicOffcanvas'; 
import RegistrationForm from '../Production/Form'; 
import { GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";
import { useUser } from "../../Context/UserContext";
import { toast } from 'react-toastify';

// ✅ 1. Import the background image (Adjust path as per your folder structure)
// If your image is in 'src/Images/map.png', use this:
import mapBackground from '../../Images/farm.jpeg'; 

const WelcomeDashboard = () => {
  const navigate = useNavigate();
  const { token, logout } = useUser();
  
  // State for Form Drawer
  const [openForm, setOpenForm] = useState(false);
  const [productionTypes, setProductionTypes] = useState([]);
  const [productionCenters, setProductionCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [productionTypeList, setProductionTypeList] = useState([]);


  // Fetch necessary data for the Registration Form
  const fetchDropdownData = useCallback(async () => {
    try {
      setDataLoading(true);
      const typesUrl = `${API_URLS.ProductionCenterType}`;
      const centersUrl = `${API_URLS.ProductionCenter}?page=1`;

      const [typesRes, centersRes] = await Promise.all([
        GetApi(typesUrl, token),
        GetApi(centersUrl, token)
      ]);

      setProductionTypes(typesRes?.results || []);
      setProductionCenters(centersRes?.results || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    } finally {
      setDataLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchDropdownData();
    }
  }, [token, fetchDropdownData]);

  const handleOpenForm = () => {
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  // Handle Success: Logout and Redirect to Login
  const handleRegistrationSuccess = async () => {
    setOpenForm(false);
    toast.success("Production Center Registered Successfully! Please log in again to continue.");
    logout();
    navigate('/Home', { replace: true });
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#33691e' }} />
      </Box>
    );
  }


  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '92vh', // Full viewport height
        width:"100%",
          backgroundImage: `url(${mapBackground})`, 
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed' // Optional: Parallax effect
        }}
      >
        

        <Paper 
          elevation={12} // Increased elevation for contrast
          sx={{ 
            textAlign: 'center', 
            p: { xs: 4, md: 6 }, 
            maxWidth: 650, 
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 1, // Ensure paper is above overlay
            backdropFilter: 'blur(17px)', // Glassmorphism effect
            background:"transparent"
          }}
        >
          {/* Decorative Top Border */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: '#ffc022' }} />

          <Box 
            sx={{ 
              width: 100, 
              height: 100, 
              bgcolor: 'transparent', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              margin: '0 auto 32px',
            }}
          >
            <img 
              src="/TN.png" 
              alt="Logo" 
              style={{ height: '100px', objectFit: 'contain', marginRight: '10px' }} 
            />
          </Box>
          
          <Typography variant="h5" fontWeight={800} color="#ffffff" gutterBottom sx={{ letterSpacing: '-0.5px' }}>
            Welcome
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
            You are registered as a <b style={{ color: '#f8d06b' }}>Production Center Manager</b>. 
            <br />
            To proceed, please complete your production center registration to start managing your inventory and saplings.
          </Typography>

          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleOpenForm}
            sx={{
              bgcolor: '#ffc022',
              color: '#000',
              fontWeight: 700,
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              borderRadius: 2,
              boxShadow: '0 4px 15px rgba(255, 192, 34, 0.3)',
              '&:hover': { 
                bgcolor: '#ff9800',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(255, 192, 34, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Register Production Center
          </Button>
        </Paper>
      </Box>

      {/* Registration Form Offcanvas */}
      <DynamicOffcanvas
        open={openForm}
        onClose={handleCloseForm}
        title="Production Center Registration"
        width={900} // Increased width to match form requirements
        actions={null}
      >
        <RegistrationForm 
          open={openForm}
          onClose={handleCloseForm}
          productionTypes={productionTypes}
          productionTypeList={productionTypeList} 
          productionCenters={productionCenters}
          selectedRegistration={null}
          actionType="create"
          onSuccess={handleRegistrationSuccess}
        />
      </DynamicOffcanvas>
    </>
  );
};

export default WelcomeDashboard;